import { Alert, Tab, Tabs } from '@mui/material';
import { TabPanel, HtmlTypography } from '@orcha/micro-library-core-ui';
import { ScoreSummary } from '../AppDetails/ScoreSummary';
import type { IPlatform, IDynamicContent, IScoreSection } from 'common-types';
import { useState } from 'react';
import useAppDetails from '../AppDetailsState';
import { platformNameConverter } from '../common-utilities';

const a11yProps = (index: number) => {
  return {
    id: `score-details-tab-${index}`,
    'aria-controls': `score-details-tab-${index}`,
  };
};

export type AccreditationTabsProps = IPlatform & {
  platformName: string;
};

export const AccreditationTabs = ({ platformName }: AccreditationTabsProps) => {
  const { app, eventHandlers, dynamicContent } = useAppDetails();
  const [selectedAccreditationTab, setSelectedAccreditationTab] = useState(0);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const { handleRequestDynamicContent } = eventHandlers;
  const currentPlatform = app?.platforms.find(
    (p) => platformNameConverter(p.platformName)?.toLowerCase() === platformName
  );
  if (currentPlatform === undefined && (app?.platforms.length ?? 0) > 0) {
    console.log(
      `Platform "${platformName}" not found. Available platforms: ${app?.platforms.map((p) => p.platformName).join(', ')}`
    );
  }
  const { accreditations } = currentPlatform ?? {};

  if (dynamicContent && isLoadingContent) {
    setIsLoadingContent(false);
  }

  const handleAccreditationTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    if (accreditations && accreditations[newValue]) {
      setSelectedAccreditationTab(newValue);
      if (accreditations[newValue].reviewId) {
        handleRequestDynamicContent?.(accreditations[newValue].reviewId);
      }
    }
  };

  const selectedAccreditation =
    accreditations?.[selectedAccreditationTab] ?? undefined;

  return (
    <>
      <Tabs
        sx={{ display: { xs: 'none', sm: 'flex' } }}
        value={selectedAccreditationTab}
        onChange={handleAccreditationTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {accreditations &&
          accreditations.map((accreditation, index) => (
            <Tab
              key={accreditation.name}
              label={accreditation.name}
              data-testid={`app-details-accreditations-tab-${accreditation.name}`}
              {...a11yProps(index)}
            />
          ))}
      </Tabs>
      {accreditations && (
        <TabPanel
          value={selectedAccreditationTab}
          index={selectedAccreditationTab}
        >
          <ScoreSummary
            reviewResultType={
              selectedAccreditation?.scoreSummary?.reviewResultType
            }
            scoreSections={(selectedAccreditation?.scoreSections ?? []).map(
              (s) => {
                return {
                  name: s.name ?? '',
                  score: s.scoreValue ?? 0,
                  resultType: s.resultType,
                };
              }
            )}
            scoreSummary={{
              accreditationLogo: selectedAccreditation?.logoUrl ? (
                <img
                  src={selectedAccreditation.logoUrl}
                  alt={selectedAccreditation.name}
                  style={{ maxHeight: '70px', maxWidth: '70px' }}
                />
              ) : undefined,
              score:
                selectedAccreditation?.scoreSummary?.scoreValue ?? undefined,
            }}
          />
          <ScoreSectionTabs
            sections={selectedAccreditation?.scoreSections ?? []}
            dynamicContent={dynamicContent}
          />
        </TabPanel>
      )}
    </>
  );
};

const ScoreSectionTabs = ({
  sections,
  dynamicContent,
}: {
  sections: IScoreSection[];
  dynamicContent?: IDynamicContent;
}) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <>
      <Tabs
        sx={{ display: { xs: 'none', sm: 'flex' } }}
        value={selectedTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {sections.map((section, index) => (
          <Tab
            key={section.name}
            label={section.name}
            data-testid={`app-details-score-details-tab-${section.name}`}
            {...a11yProps(index)}
          />
        ))}
      </Tabs>
      <TabPanel value={selectedTab} index={selectedTab}>
        <DynamicContentPanel
          dynamicContent={dynamicContent}
          selectedSectionName={sections[selectedTab]?.name ?? ''}
        />
      </TabPanel>
    </>
  );
};

const DynamicContentPanel = ({
  dynamicContent,
  selectedSectionName,
}: {
  dynamicContent?: IDynamicContent;
  selectedSectionName: string;
}) => {
  const sectionContent = dynamicContent?.dynamicContentSection.find(
    (section) => section.sectionName === selectedSectionName
  );

  if (!sectionContent) {
    return (
      <Alert severity="info">
        No dynamic content available for this section.
      </Alert>
    );
  }
  const sortedContent = sectionContent.htmlContent
    .sort((a, b) => a.order - b.order)
    .map((dc) => dc.content)
    .join('')
    .trim();
  return <HtmlTypography bodyHtml={sortedContent} />;
};
